import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Copy, Check, Key } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Select } from "@/components/ui/Select.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Modal } from "@/components/ui/Modal.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { adminApi, type AdminApiKey } from "@/api/admin.ts";

export function AdminApiKeysPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "api-keys"],
    queryFn: () => adminApi.listApiKeys(),
  });

  const { data: tenantData } = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: () => adminApi.listTenants(),
  });
  const tenants = tenantData?.tenants ?? [];

  const create = useMutation({
    mutationFn: () => adminApi.createApiKey({ tenantId, name: name.trim() || "default" }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "api-keys"] });
      setNewKey(res.key);
      setShowCreate(false);
      setName("");
      setTenantId("");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteApiKey(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "api-keys"] }); setDeleteId(null); },
  });

  const keys: AdminApiKey[] = data?.keys ?? [];

  const copyKey = async () => {
    if (!newKey) return;
    await navigator.clipboard.writeText(newKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-4xl p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-surface-900">API Keys</h1>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-1" /> New Key
        </Button>
      </div>

      <div className="space-y-3">
        {keys.length === 0 && (
          <Card><CardContent className="py-8 text-center text-surface-400 text-sm">No API keys created yet.</CardContent></Card>
        )}
        {keys.map((k) => (
          <Card key={k.id}>
            <CardContent className="py-4 flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Key className="w-3.5 h-3.5 text-surface-400" />
                  <span className="font-medium text-sm text-surface-900">{k.name}</span>
                  <Badge variant={k.isActive ? "success" : "muted"}>{k.isActive ? "Active" : "Revoked"}</Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-surface-500">
                  <span className="font-mono">{k.keyPrefix}…</span>
                  <span>Tenant: {k.tenantName}</span>
                  <span>Created {new Date(k.createdAt).toLocaleDateString()}</span>
                  {k.lastUsedAt && <span>Last used {new Date(k.lastUsedAt).toLocaleDateString()}</span>}
                </div>
              </div>
              <Button variant="ghost" size="sm" aria-label="Revoke this key" onClick={() => setDeleteId(k.id)}>
                <Trash2 className="w-3.5 h-3.5 text-red-500" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create API Key" size="sm">
        <div className="space-y-4">
          <Select
            label="Tenant"
            placeholder="Select a tenant"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            options={tenants.map((t) => ({ value: t.id, label: `${t.name} (${t.slug})` }))}
            hint="The key is scoped to this tenant. Service users created via this key belong to it."
          />
          <div>
            <label className="block text-xs font-medium text-surface-600 mb-1">Key Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SellerZoom, Production" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !tenantId}>
            {create.isPending ? <Spinner className="w-4 h-4" /> : "Generate Key"}
          </Button>
        </div>
      </Modal>

      {/* Show new key modal (one-time display) */}
      <Modal
        open={newKey !== null}
        onClose={() => setNewKey(null)}
        title="API Key Created"
        size="sm"
      >
        <p className="text-sm text-surface-600 mb-3">Copy this key now — it won't be shown again.</p>
        <div className="flex items-center gap-2 p-3 bg-surface-50 rounded-lg border">
          <code className="flex-1 text-xs font-mono text-surface-900 break-all select-all">{newKey}</code>
          <Button variant="ghost" size="sm" aria-label={copied ? "Copied" : "Copy the key"} onClick={copyKey}>
            {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={() => setNewKey(null)}>Done</Button>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal open={deleteId !== null} onClose={() => setDeleteId(null)} title="Revoke API Key" size="sm">
        <p className="text-sm text-surface-600 mb-4">Are you sure? Any integrations using this key will stop working immediately.</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => deleteId && remove.mutate(deleteId)} disabled={remove.isPending}>
            {remove.isPending ? <Spinner className="w-4 h-4" /> : "Revoke"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
